import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../Icon';
import { Avatar } from '../Avatar';
import '../../styles/assignees-picker.css';

/**
 * Assigned — a multi-person picker. The picked people are chips inside one 34px field; typing in the
 * field filters a listbox of the remaining people (portal, so a rail card or table cannot clip it).
 *
 * Keyboard: ArrowDown/ArrowUp move through the list (opening it), Enter adds the active person,
 * Escape closes, Backspace in an empty field removes the last chip. Each chip has its own remove button.
 * `restrictTo` (new members) limits the choices to those emails; chips already set stay removable.
 * `onAdd` / `onRemove` may be async (the detail page saves each change); the field is busy meanwhile.
 */
export type AssigneeOption = { email: string; label?: string };

const GAP = 4;
const PANEL_MAX = 280;
interface Placement {
  left: number;
  top: number;
  width: number;
  up: boolean;
  maxHeight: number;
}
function place(rect: DOMRect): Placement {
  const width = Math.max(220, Math.min(rect.width, window.innerWidth - 16));
  const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
  const below = window.innerHeight - rect.bottom;
  const above = rect.top;
  const up = below < 200 && above > below;
  return {
    left,
    width,
    up,
    top: up ? rect.top - GAP : rect.bottom + GAP,
    maxHeight: Math.min(PANEL_MAX, (up ? above : below) - 12),
  };
}

const initials = (email: string) => {
  const local = email.split('@')[0] ?? email;
  const parts = local.split(/[._-]+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? parts[0]?.[1] ?? '')).toUpperCase() || '?';
};

export function AssigneesPicker({
  value,
  options,
  onAdd,
  onRemove,
  disabled,
  restrictTo,
  id,
  ariaLabel,
}: {
  value: string[];
  options: AssigneeOption[];
  onAdd: (email: string) => void | Promise<void>;
  onRemove: (email: string) => void | Promise<void>;
  disabled?: boolean;
  /** When set, only these emails may be picked (new members: default access assignees). */
  restrictTo?: string[];
  id?: string;
  ariaLabel?: string;
}) {
  const autoId = useId();
  const inputId = id ?? `asg-${autoId}`;
  const listId = `${inputId}-list`;
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [busy, setBusy] = useState(false);
  const [pos, setPos] = useState<Placement | null>(null);
  const fieldRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const off = !!disabled || busy;

  const chosen = useMemo(() => new Set(value.map((e) => e.toLowerCase())), [value]);
  const allowed = useMemo(() => (restrictTo ? new Set(restrictTo.map((e) => e.toLowerCase())) : null), [restrictTo]);
  const labelOf = useMemo(() => {
    const m = new Map(options.map((o) => [o.email.toLowerCase(), o.label || o.email]));
    return (email: string) => m.get(email.toLowerCase()) ?? email;
  }, [options]);

  const ql = q.trim().toLowerCase();
  const choices = useMemo(() => {
    const seen = new Set<string>();
    return options
      .filter((o) => {
        const e = o.email.toLowerCase();
        if (seen.has(e) || chosen.has(e) || (allowed && !allowed.has(e))) return false;
        seen.add(e);
        return !ql || e.includes(ql) || (o.label ?? '').toLowerCase().includes(ql);
      })
      .sort((a, b) => (a.label || a.email).localeCompare(b.label || b.email));
  }, [options, chosen, allowed, ql]);

  useEffect(() => setActive(0), [ql, open]);
  const reposition = () => {
    if (fieldRef.current) setPos(place(fieldRef.current.getBoundingClientRect()));
  };
  useLayoutEffect(() => {
    if (open) reposition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, value.length]);
  useEffect(() => {
    if (!open) return;
    const onMove = () => reposition();
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (fieldRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    window.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
      window.removeEventListener('mousedown', onDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  // Keep the active option in view while arrowing through a long list.
  useEffect(() => {
    if (!open) return;
    panelRef.current?.querySelector(`[data-idx="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const run = async (fn: () => void | Promise<void>) => {
    setBusy(true);
    try {
      await fn();
    } catch {
      /* the caller reports its own errors */
    } finally {
      setBusy(false);
    }
  };
  const add = (email: string) => {
    setQ('');
    void run(() => onAdd(email)).then(() => inputRef.current?.focus());
  };
  const remove = (email: string) => {
    void run(() => onRemove(email)).then(() => inputRef.current?.focus());
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      if (!choices.length) return;
      setActive((i) => (i + (e.key === 'ArrowDown' ? 1 : -1) + choices.length) % choices.length);
    } else if (e.key === 'Enter') {
      // Never submit the surrounding form from inside the picker.
      e.preventDefault();
      const pick = open ? choices[active] : undefined;
      if (pick) add(pick.email);
      else setOpen(true);
    } else if (e.key === 'Escape') {
      if (open) {
        e.preventDefault();
        e.stopPropagation();
        setOpen(false);
      }
    } else if (e.key === 'Backspace' && !q && value.length) {
      remove(value[value.length - 1]);
    }
  };

  const emptyText =
    allowed && !allowed.size
      ? 'No default assignees configured'
      : ql
        ? 'No matching people'
        : options.length
          ? 'Everyone is assigned'
          : 'No people to pick';
  const panel =
    open && pos && !off
      ? createPortal(
          <div
            ref={panelRef}
            className="asg-panel"
            style={{
              left: pos.left,
              top: pos.top,
              width: pos.width,
              maxHeight: pos.maxHeight,
              transform: pos.up ? 'translateY(-100%)' : undefined,
            }}
          >
            {restrictTo && <div className="asg-panel__note">Default access assignees</div>}
            <ul className="asg-list" role="listbox" id={listId} aria-label={ariaLabel ?? 'People'}>
              {choices.map((o, i) => (
                <li
                  key={o.email}
                  id={`${listId}-${i}`}
                  data-idx={i}
                  role="option"
                  aria-selected={i === active}
                  className={`asg-opt${i === active ? ' asg-opt--active' : ''}`}
                  onMouseEnter={() => setActive(i)}
                  // mousedown, not click: keep focus in the input.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    add(o.email);
                  }}
                >
                  <Avatar email={o.email} className="asg-avatar" text={initials(o.email)} />
                  <span className="asg-opt__name">{o.label || o.email}</span>
                  {o.label && o.label !== o.email && <span className="asg-opt__email">{o.email}</span>}
                </li>
              ))}
              {!choices.length && <li className="asg-empty">{emptyText}</li>}
            </ul>
          </div>,
          document.body,
        )
      : null;

  return (
    <div
      ref={fieldRef}
      className={`asg-field${off ? ' asg-field--off' : ''}${open ? ' asg-field--open' : ''}`}
      data-testid="assignees-picker"
      onMouseDown={(e) => {
        // A click anywhere in the field (not on a chip button) focuses the search and opens the list.
        if ((e.target as HTMLElement).closest('button') || off) return;
        if (e.target !== inputRef.current) e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }}
    >
      {value.map((email) => (
        <span key={email} className="asg-chip" data-testid="assignee-chip" title={email}>
          <Avatar email={email} className="asg-avatar" text={initials(email)} />
          <span className="asg-chip__name">{labelOf(email)}</span>
          <button
            type="button"
            className="asg-chip__x"
            aria-label={`Remove ${email}`}
            disabled={off}
            onClick={() => remove(email)}
          >
            <Icon name="close" size={11} />
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        id={inputId}
        className="asg-input"
        role="combobox"
        aria-label={ariaLabel ?? 'Assigned'}
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && choices[active] ? `${listId}-${active}` : undefined}
        autoComplete="off"
        placeholder={value.length ? 'Add…' : 'Add people…'}
        value={q}
        disabled={off}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={onKeyDown}
      />
      <span className="asg-caret" aria-hidden="true" />
      {panel}
    </div>
  );
}
